import React, { useState, useMemo } from 'react';
import Header from '../components/layout/Header';
import ErrorBanner from '../components/ui/ErrorBanner';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import KPICard from '../components/ui/KPICard';
import { useMemberships } from '../hooks/useMemberships';
import { toLocalDateStr } from '../utils/formatters';
import apiClient from '../api/apiClient';

function today() { return toLocalDateStr(new Date()); }
const toStr = toLocalDateStr;
function monthStart() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
function lastMonthStart() {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return toStr(d);
}
function lastMonthEnd() {
  const d = new Date(); d.setDate(0); return toStr(d);
}

const RANGES = [
  { label: 'This Month', start: monthStart, end: today },
  { label: 'Last Month', start: lastMonthStart, end: lastMonthEnd },
  { label: 'YTD', start: () => `${new Date().getFullYear()}-01-01`, end: today },
  { label: 'Custom', start: null, end: null },
];

export default function MembershipPage() {
  const [rangeIdx, setRangeIdx] = useState(0);
  const [customStart, setCustomStart] = useState(monthStart());
  const [customEnd, setCustomEnd] = useState(today());
  const isCustom = rangeIdx === RANGES.length - 1;

  const { start, end } = useMemo(() => {
    if (isCustom) return { start: customStart, end: customEnd };
    const r = RANGES[rangeIdx];
    return { start: r.start(), end: r.end() };
  }, [rangeIdx, isCustom, customStart, customEnd]);

  const { totals = {}, salesTable = [], recurringServices = { due: [], booked: [] }, loading, error } = useMemberships(start, end);

  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState('');
  const runFullResync = async () => {
    setResyncing(true);
    setResyncMsg('');
    try {
      await apiClient.post('/sync/memberships-full');
      setResyncMsg('Full resync started — this can take a few minutes for the full history. Reload after.');
    } catch (err) {
      setResyncMsg(err.response?.data?.error || 'Failed to start resync.');
    } finally {
      setResyncing(false);
    }
  };

  return (
    <div>
      <Header title="Membership Dashboard" />
      <div style={st.page}>
        <ErrorBanner message={error} />

        {/* Toolbar */}
        <div style={st.toolbar}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {RANGES.map((r, i) => (
              <button key={r.label} onClick={() => setRangeIdx(i)}
                style={{ ...st.rangeBtn, ...(rangeIdx === i ? st.rangeBtnActive : {}) }}>
                {r.label}
              </button>
            ))}
            {isCustom && (
              <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={st.dateInput} />
                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>→</span>
                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={st.dateInput} />
              </div>
            )}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={runFullResync} disabled={resyncing} title="Force a full historical resync of all memberships from ServiceTitan - use if Membership Portfolio looks too low" style={st.resyncBtn}>
              {resyncing ? 'Starting...' : 'Full Membership Resync'}
            </button>
            {resyncMsg && <span style={{ fontSize: '0.75rem', color: '#6366f1' }}>{resyncMsg}</span>}
            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{start} → {end}</span>
          </div>
        </div>

        {/* KPI Row */}
        <div style={st.kpiRow}>
          <KPICard title="Portfolio MRR" value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.mrr || 0)} icon="📈" color="#8b5cf6" loading={loading} sub={totals.monthly || totals.yearly ? `${totals.monthly || 0} Monthly • ${totals.yearly || 0} Yearly` : "Monthly equivalent"} />
          <KPICard title="Membership Revenue" value={new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totals.membershipRevenue || 0)} icon="💰" color="#22c55e" loading={loading} sub="All revenue from active members" />
          <KPICard title="Membership Portfolio" value={new Intl.NumberFormat('en-US').format(totals.totalPortfolio || 0)} icon="📂" color="#3b82f6" loading={loading} sub="Current active snapshot" />
          <KPICard title="New Adds" value={totals.newMemberships ?? 0} icon="➕" color="#22c55e" loading={loading} sub="Created in period" />
          <KPICard title="Cancelled" value={totals.canceledMemberships ?? 0} icon="❌" color="#ef4444" loading={loading} sub="Canceled in period" />
          <KPICard title="Suspended" value={totals.suspendedMemberships ?? 0} icon="⏸️" color="#64748b" loading={loading} sub="Current snapshot" />
          <KPICard title="Customers Due" value={totals.recurringDue ?? 0} icon="⌛" color="#d97706" loading={loading} sub="Recurring services due in period" />
          <KPICard title="Scheduled Visits" value={totals.recurringScheduled ?? 0} icon="✅" color="#10b981" loading={loading} sub="R1/R2/R3 jobs scheduled in period" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 400px', gap: '1.25rem', alignItems: 'start' }}>
          {/* Sales Table */}
          <div style={st.card}>
            <div style={st.cardHeader}>Membership Sales by Employee</div>
            {loading ? <LoadingSpinner /> : (
              <table style={st.table}>
                <thead>
                  <tr style={st.thead}>
                    <th style={st.th}>Employee</th>
                    <th style={{ ...st.th, textAlign: 'center' }}>New Sales</th>
                  </tr>
                </thead>
                <tbody>
                  {salesTable.length === 0 ? (
                    <tr><td colSpan="2" style={{ ...st.td, textAlign: 'center', padding: '2rem' }}>No sales recorded in this period</td></tr>
                  ) : salesTable.map(s => (
                    <tr key={s.name}>
                      <td style={{ ...st.td, fontWeight: '600' }}>{s.name}</td>
                      <td style={{ ...st.td, textAlign: 'center', color: '#16a34a', fontWeight: '800' }}>{s.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Recurring List */}
          <div style={st.card}>
            <div style={st.cardHeader}>Upcoming Recurring Services</div>
            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={st.listSection}>
                <div style={st.listTitle}>Due (Pending Booking)</div>
                {recurringServices.due.slice(0, 10).map(s => (
                  <div key={s.id} style={st.listItem}>
                    <div style={{ fontWeight: '700' }}>{s.customer_name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#374151' }}>{s.service_name}</div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b' }}>Due: {s.event_date ? s.event_date.slice(0, 10) : 'N/A'}</div>
                  </div>
                ))}
                {recurringServices.due.length > 10 && <div style={st.more}>+ {recurringServices.due.length - 10} more due</div>}
                {recurringServices.due.length === 0 && <div style={st.emptyText}>No services due this period</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const st = {
  page: { padding: '1.5rem 1.75rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' },
  toolbar: {
    display: 'flex', alignItems: 'center', gap: '1rem',
    background: '#fff', padding: '1rem', borderRadius: '0.75rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  rangeBtn: {
    padding: '0.45rem 0.9rem', background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600', color: '#64748b',
  },
  rangeBtnActive: { background: '#3b82f6', color: '#fff', border: '1px solid #3b82f6' },
  resyncBtn: {
    padding: '0.4rem 0.75rem', background: '#eef2ff', color: '#6366f1',
    border: '1px solid #c7d2fe', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
  },
  dateInput: {
    padding: '0.35rem 0.5rem', border: '1px solid #e2e8f0', borderRadius: '0.5rem',
    fontSize: '0.78rem', color: '#374151', background: '#fff', outline: 'none', cursor: 'pointer',
  },
  kpiRow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  card: { background: '#fff', borderRadius: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  cardHeader: {
    padding: '1rem', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderBottom: '1px solid #e2e8f0', fontSize: '0.9rem', fontWeight: '700', color: '#334155',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#f8fafc' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' },
  td: { padding: '0.85rem 1rem', fontSize: '0.875rem', borderBottom: '1px solid #f1f5f9' },
  listSection: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  listTitle: { fontSize: '0.75rem', fontWeight: '800', color: '#475569', textTransform: 'uppercase', marginBottom: '0.25rem' },
  listItem: { padding: '0.6rem', background: '#f8fafc', borderRadius: '0.4rem', border: '1px solid #f1f5f9', fontSize: '0.8rem', color: '#1e293b' },
  emptyText: { textAlign: 'center', padding: '1rem', fontSize: '0.8rem', color: '#94a3b8' },
  more: { textAlign: 'center', fontSize: '0.7rem', color: '#3b82f6', fontWeight: '600' },
};
